/**
 * PagesPage
 * Overview page showing all pages with card-based layout.
 * Replaces PageContentPage and CustomPagesPage with a unified dynamic pages system.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Home,
  Info,
  Phone,
  Car,
  HelpCircle,
  FileText,
  Plus,
  ChevronRight,
  LayoutTemplate,
  Loader2,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { usePages, useHomePage } from '@/hooks/use-pages'
import { PageCard } from '@/components/pages/PageCard'
import { CreatePageDialog } from '@/components/pages/CreatePageDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { DynamicPageType } from '@limo/shared'

// Icons for each page type
const PAGE_TYPE_ICONS: Record<DynamicPageType, typeof Home> = {
  home: Home,
  about: Info,
  contact: Phone,
  fleet: Car,
  faq: HelpCircle,
  custom: FileText,
}

export function PagesPage() {
  const navigate = useNavigate()
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // Use impersonated account when admin is impersonating
  const activeAccount = isImpersonating ? impersonatedAccount : account
  const accountId = activeAccount?.id

  // Get the base path for navigation
  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Build viewer URL for preview
  const viewerBase = import.meta.env.VITE_VIEWER_URL || 'http://localhost:3001'
  const viewerUrl = viewerBase.replace(
    'localhost',
    `${activeAccount?.slug}.localhost`
  )

  // Fetch pages and home page setting
  const { data: pages, isLoading: pagesLoading } = usePages(accountId)
  const { data: homePageId, isLoading: homePageLoading } = useHomePage(accountId)

  const isLoading = pagesLoading || homePageLoading

  // Sort pages with home page first
  const sortedPages = useMemo(() => {
    if (!pages || !homePageId) return pages
    return [...pages].sort((a, b) => {
      // Home page always first
      if (a.id === homePageId) return -1
      if (b.id === homePageId) return 1
      // Keep original order for other pages
      return 0
    })
  }, [pages, homePageId])

  // Navigate to page editor
  const handleEditPage = (pageId: string) => {
    navigate(`${basePath}/pages/${pageId}`)
  }

  // Handle successful page creation
  const handlePageCreated = (pageId: string) => {
    setCreateDialogOpen(false)
    navigate(`${basePath}/pages/${pageId}`)
  }

  // Get preview URL for a page
  const getPreviewUrl = (slug: string) => {
    return `${viewerUrl}${slug ? `/${slug}` : ''}`
  }

  // Get icon for page type
  const getPageIcon = (pageType: DynamicPageType) => {
    return PAGE_TYPE_ICONS[pageType] || FileText
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pages</h1>
          <p className="text-muted-foreground">
            Manage the pages on your website
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <a href={viewerUrl} target="_blank" rel="noopener noreferrer">
              View Website
              <ChevronRight className="ml-1 h-4 w-4" />
            </a>
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Page
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950">
        <div className="flex items-start gap-3">
          <LayoutTemplate className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h3 className="font-medium text-blue-900 dark:text-blue-100">
              How Pages Work
            </h3>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              Each page is built from sections that you can add, remove, and
              reorder. Click on a page to edit its sections and content. Toggle
              the switch to show or hide a page on your website.
            </p>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (!sortedPages || sortedPages.length === 0) && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No pages yet</h3>
          <p className="mt-1 text-muted-foreground">
            Get started by creating your first page.
          </p>
          <Button onClick={() => setCreateDialogOpen(true)} className="mt-4">
            <Plus className="mr-2 h-4 w-4" />
            Create Page
          </Button>
        </div>
      )}

      {/* Pages Grid */}
      {!isLoading && sortedPages && sortedPages.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sortedPages.map((page) => (
            <PageCard
              key={page.id}
              page={page}
              isHomePage={page.id === homePageId}
              onEdit={() => handleEditPage(page.id)}
              getPreviewUrl={getPreviewUrl}
              getPageIcon={getPageIcon}
              basePath={basePath}
            />
          ))}

          {/* Add Page Card */}
          <Card
            className="border-dashed cursor-pointer hover:border-primary hover:bg-muted/50 transition-all"
            onClick={() => setCreateDialogOpen(true)}
          >
            <CardContent className="flex flex-col items-center justify-center h-full py-12">
              <div className="rounded-full bg-muted p-3">
                <Plus className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="mt-3 text-sm text-muted-foreground">Add New Page</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Page Dialog */}
      <CreatePageDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        accountId={accountId || ''}
        onSuccess={handlePageCreated}
      />
    </div>
  )
}
